import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bot, CheckCircle2, ChevronDown, FileWarning, Flag, Hourglass, ListChecks, ListTodo, Scale, ShieldAlert, Sparkles, type LucideIcon } from "lucide-react";
import { useApi, type AiProposal, type PagedResult } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, Sheet, SheetBody, SheetContent, SheetHeader, SheetTitle, cn, useSave } from "@ncp/ui";
import { ProposalList } from "@ncp/assistant-sdk";
import { ResolveChecklistItemDialog } from "../ResolveChecklistItemDialog";
import { OverrideFindingDialog, type FindingToOverride } from "../review/OverrideFindingDialog";
import { ConfidenceMeter } from "../review/CriterionCard";
import { findingOf, type ReviewView, type StageChecklist, type StageChecklistItem, type StageChecklistKind } from "../review/api";
import { useConfirmStep, useStepLettersRefusal } from "./StepLetters";
import { useRefuseRequest } from "./RefuseRequest";

const KIND_ICON: Record<StageChecklistKind, LucideIcon> = { Criterion: Scale, Issue: Flag, Document: FileWarning, Task: ListTodo, Draft: Sparkles, Condition: Hourglass, Finding: ShieldAlert };
const FIRST_SHOWN = 6;

/**
 * One list per stage of what is still to be done: criteria to decide, findings of the platform that hold the stage, issues to resolve, filings
 * missing, tasks, drafts awaiting review and the conditions of the procedure. Every count is a count of what is listed (7.4): the heading counts
 * the rows below it, and the items needed to conclude carry the tag the close button counts. Every item is one click from where it is done.
 */
export function StageChecklistCard({ caseId, checklist, review, onSelectCriterion, onSelectIssue, onOpenDocuments, onOpenSurface }: {
  caseId: string; checklist: StageChecklist; review?: ReviewView; onSelectCriterion: (key: string) => void; onSelectIssue: (target: string) => void; onOpenDocuments: () => void;
  /** Opens a workspace surface, such as the case file's letters for a letter that waits for the officer's confirmation. */
  onOpenSurface?: (surface: string) => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const qc = useQueryClient();
  const ar = locale === "ar";
  const [all, setAll] = useState(false);
  const [resolving, setResolving] = useState<string | null>(null);
  const [draft, setDraft] = useState<string | null>(null);
  const [overriding, setOverriding] = useState<FindingToOverride | null>(null);
  // A complainant's request to withdraw is the Department's to grant or refuse; a refusal carries reasons the complainant is told (Article 22(1)).
  const refuse = useRefuseRequest(caseId);
  const proposals = useQuery({ queryKey: ["case-proposals", caseId], queryFn: () => api.get<PagedResult<AiProposal>>("/api/ai/proposals", { caseId, pageSize: 100 }), enabled: draft !== null });
  const refused = useStepLettersRefusal();
  const completeTask = useSave({
    mutationFn: ({ taskId, lettersFingerprint }: { taskId: string; lettersFingerprint: string | null }) => api.post(`/api/cases/${caseId}/workflow/tasks/${taskId}/complete`, { lettersFingerprint }),
    // A step the task takes and is refused is reported beside "Task completed", with its reason, by the save helper (S 2.8).
    saved: t("backoffice:workspace.taskCompleted"),
    failed: t("backoffice:stage.left.taskFailed"),
    onSuccess: () => { for (const k of [["case", caseId], ["case-workflow", caseId], ["case-review", caseId], ["case-tasks", caseId], ["case-events", caseId]]) void qc.invalidateQueries({ queryKey: k }); },
    onError: (e) => { refused(e); },
  });
  // A task whose completion takes a step that sends letters shows them in full first (release 1.6).
  const step = useConfirmStep(caseId);

  if (!checklist.reviewOpen) return null;
  // Needed items first, in the server's order within each group; each row is one item, so the heading counts exactly what is shown.
  const items = [...checklist.outstanding].sort((a, b) => Number(b.blocking) - Number(a.blocking));
  const shown = all ? items : items.slice(0, FIRST_SHOWN);
  const needed = checklist.needed ?? items.filter((i) => i.blocking).length;

  const act = (i: StageChecklistItem) => {
    switch (i.kind) {
      case "Criterion": onSelectCriterion(i.key); break;
      case "Issue": onSelectIssue(i.target); break;
      case "Document": if (review?.criteria.some((c) => c.aiSource === "completeness")) setResolving(i.key); else onOpenDocuments(); break;
      // A task done on a surface (a held letter is confirmed in the case file's letters) opens it; any other task is completed in place.
      case "Task": if (i.surface?.startsWith("file:") && onOpenSurface) onOpenSurface(i.surface); else void step.run({ taskId: i.key }, (lettersFingerprint) => completeTask.mutate({ taskId: i.key, lettersFingerprint })); break;
      case "Draft": setDraft(i.key); break;
      case "Finding": {
        const f = findingOf(i.target);
        const full = review?.binding?.find((b) => f && b.kind === f.kind && b.subjectKey === f.subjectKey);
        if (f) setOverriding({ ...f, title: i.title, titleAr: i.titleAr, detail: i.detail, detailAr: i.detailAr, overridable: full?.overridable ?? true });
        break;
      }
      default: break;
    }
  };
  const draftProposal = draft ? proposals.data?.items.find((p) => p.id === draft) : undefined;
  /** The platform's reading of a criterion row: its verdict and how sure it is – never before the officer's own view while that setting is on (4.5, 7.12). */
  const aiOf = (i: StageChecklistItem) => {
    if (i.kind !== "Criterion" || review?.officerViewFirst !== false) return null;
    const ai = review?.criteria.find((c) => c.key === i.key)?.ai;
    return ai && ai.verdict !== "Unknown" ? ai : null;
  };

  return (
    <Card className="overflow-hidden" aria-labelledby="stage-left-title">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3">
        <ListChecks className="size-5 shrink-0 text-aegold-700" aria-hidden />
        <h2 id="stage-left-title" className="font-heading text-base font-semibold text-aeblack-950">{t("backoffice:stage.left.title")}</h2>
        {items.length > 0 ? <span className="text-sm text-sand-800">{t("backoffice:stage.left.count", { count: items.length })}{needed > 0 ? <> · <span className="text-warning-800">{t("backoffice:r3Process.left.needed", { count: needed })}</span></> : null}</span> : null}
        {checklist.done > 0 ? <span className="ms-auto inline-flex items-center gap-1.5 text-sm text-success-800"><CheckCircle2 className="size-4" /> {t("backoffice:stage.left.done", { count: checklist.done })}</span> : null}
      </div>
      {items.length === 0 ? (
        <p className="border-t border-sand-100 px-4 py-3 text-sm text-sand-900">{checklist.canConclude ? t("backoffice:stage.left.noneCanConclude") : t("backoffice:stage.left.none")}</p>
      ) : (
        <>
          <ul className="divide-y divide-sand-100 border-t border-sand-100">
            {shown.map((i) => {
              const Icon = KIND_ICON[i.kind] ?? Scale;
              const title = ar ? i.titleAr : i.title;
              const detail = i.kind === "Issue" ? null : ar ? i.detailAr ?? i.detail : i.detail;
              const clickable = i.kind !== "Condition";
              const action = i.kind === "Finding" ? t("backoffice:r3Process.finding.open") : i.kind === "Task" && i.surface === "confirm" ? t("backoffice:r3Process.left.confirm") : t(`backoffice:stage.left.actions.${i.kind}`, { defaultValue: "" });
              const body = (
                <>
                  <Icon className={cn("size-4 shrink-0", i.blocking ? "text-warning-700" : "text-sand-700")} aria-hidden />
                  <span className="min-w-0 flex-1" title={detail ?? undefined}>
                    <span className="block truncate text-sm text-aeblack-900" dir="auto">{title}</span>
                    {detail ? <span className="block truncate text-xs text-muted-foreground" dir="auto">{detail}</span> : null}
                  </span>
                  {aiOf(i) ? <AiReading verdict={aiOf(i)!.verdict} confidence={aiOf(i)!.confidence} /> : null}
                  {i.blocking ? <NeededTag /> : null}
                  {clickable && action ? <span className="shrink-0 text-xs font-medium text-aegold-800">{action}</span> : null}
                </>
              );
              return (
                <li key={`${i.kind}:${i.key}`} className={i.refusable ? "flex items-center" : undefined}>
                  {clickable ? (
                    <button type="button" data-checklist-item={i.kind} onClick={() => act(i)} disabled={i.kind === "Task" && (completeTask.isPending || step.checking)} className="flex w-full min-w-0 items-center gap-3 px-4 py-2 text-start transition-colors hover:bg-sand-50 disabled:opacity-60">{body}</button>
                  ) : <div className="flex items-center gap-3 px-4 py-2">{body}</div>}
                  {i.refusable ? <Button variant="ghost" size="sm" className="me-2 shrink-0" onClick={() => refuse.open(i)}>{t("backoffice:r3Process.left.refuse")}</Button> : null}
                </li>
              );
            })}
          </ul>
          {items.length > FIRST_SHOWN ? (
            <button type="button" aria-expanded={all} onClick={() => setAll((v) => !v)} className="flex w-full items-center justify-center gap-1.5 border-t border-sand-100 px-4 py-2 text-xs font-medium text-aegold-800 hover:bg-sand-50">
              {all ? t("backoffice:stage.left.showFewer") : t("backoffice:stage.left.showAll", { count: items.length })} <ChevronDown className={cn("size-3.5 transition-transform", all && "rotate-180")} />
            </button>
          ) : null}
        </>
      )}
      {step.dialog}
      <ResolveChecklistItemDialog caseId={caseId} itemKey={resolving} open={resolving !== null} onOpenChange={(o) => { if (!o) setResolving(null); }} />
      {refuse.dialog}
      <OverrideFindingDialog caseId={caseId} finding={overriding} mayOverride={!!(checklist.mayOverride ?? review?.mayOverride)} open={overriding !== null} onOpenChange={(o) => { if (!o) setOverriding(null); }} />
      <Sheet open={draft !== null} onOpenChange={(o) => { if (!o) setDraft(null); }}>
        <SheetContent width="max-w-2xl">
          <SheetHeader><SheetTitle>{t("backoffice:stage.left.kinds.Draft")}</SheetTitle></SheetHeader>
          <SheetBody>
            {draftProposal ? <ProposalList proposals={[draftProposal]} defaultOpenSingle invalidate={[["case", caseId], ["case-proposals", caseId], ["case-review", caseId], ["case-workflow", caseId], ["proposals"], ["proposals-summary"]]} /> : <Button variant="ghost" loading>{t("misc.loading")}</Button>}
          </SheetBody>
        </SheetContent>
      </Sheet>
    </Card>
  );
}

/** The platform's verdict on a criterion and its confidence, beside the step. */
function AiReading({ verdict, confidence }: { verdict: string; confidence: number }) {
  const { t } = useTranslation();
  return (
    <span className="hidden shrink-0 items-center gap-2 rounded-full border border-seablue-100 bg-seablue-50/70 py-0.5 pe-2 ps-2.5 text-[11px] text-seablue-900 sm:inline-flex">
      <Bot className="size-3.5 text-seablue-700" aria-hidden />
      <span className="max-w-32 truncate">{t(`backoffice:review.verdicts.${verdict}`)}</span>
      <ConfidenceMeter value={confidence} compact />
    </span>
  );
}

function NeededTag() {
  const { t } = useTranslation();
  return <Badge size="sm" variant="warning" className="shrink-0">{t("backoffice:stage.left.blocking")}</Badge>;
}
